/**
 * Delinquencies Screen
 *
 * Computes and displays the delinquency ratio time series --
 * the proportion of receivables that are past-due relative to gross sales.
 * This is a key input for determining reserve requirements in ABL facilities.
 *
 * Data flow:
 *  - Reads `data.delinquencies` and `project.hasDelinquencies` from Redux.
 *  - CalcParamsModal lets the user adjust delinquency-specific calculation
 *    parameters (e.g., lookback periods) before running.
 *  - "Calculate" dispatches to the Python `receivables_delinquencies` module.
 *  - "Gross Sales (Delinquency Ratio)" uses a configurable rolling window
 *    that differs from the raw gross sales figure.
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateDelinquencies, getDelinquenciesData } from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";
import CalcParamsModal, { ParamsButton } from "Components/CalcParamsModal";
import { DELINQUENCIES_FIELDS } from "Config/calcParamsConfig";

const headers = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Gross Sales", key: "gross_sales", datatype: "float" },
  { text: "Gross Sales (Delinquency Ratio)", key: "gross_sales_for_delinquency_ratio", datatype: "float" },
];

const Delinquencies = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);
  const [showParams, setShowParams] = useState(false);

  const { data: delinquencies, has: hasDelinquencies } = useAnalysis("delinquencies", getDelinquenciesData);
  const hasMaster = useScenarioFlag("hasMaster");

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      {showParams && (
        <CalcParamsModal
          section="delinquencies"
          title="Delinquencies Parameters"
          fields={DELINQUENCIES_FIELDS}
          onClose={() => setShowParams(false)}
        />
      )}
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateDelinquencies(() => setCalculating(false)));
          }}
        >
          {hasDelinquencies ? "Recalculate" : "Calculate"} Delinquencies
        </button>
        <ParamsButton onClick={() => setShowParams(true)} />
      </div>
      {hasDelinquencies && !delinquencies && <SkeletonTable />}
      {delinquencies && (
        <DataTable
          data={delinquencies.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default Delinquencies;
